import { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent, markStep } from '@cbhq/client-analytics';

import { useStepUpSubmission } from '../../../hooks/useStepUpSubmission';
import { rumAddAction } from '../../../utils/analytics/datadogRum';
import { RemoveMemberAlert } from '../components/RemoveMemberAlert';
import { StepUpTwoFactorModal } from '../components/StepUpTwoFactorModal';
import { useTeamApi } from '../useTeamApi';
import type { Member } from '../utils/constants';
import { getDisplayName } from '../utils/helpers';
import { hasGatedRole } from '../utils/stepUp';

type RemoveMemberProps = {
  visible: boolean;
  member: Member;
  organizationId: string;
  onClose: () => void;
  onSuccess?: () => void;
};

const messages = defineMessages({
  removeMemberModalSuccess: {
    id: 'Team.removeMemberModalSuccess',
    defaultMessage: 'Member removed',
    description: 'Remove member modal success',
  },
  removeMemberModalError: {
    id: 'Team.removeMemberModalError',
    defaultMessage: 'Error removing member',
    description: 'Remove member modal error',
  },
});

export function RemoveMember({
  visible,
  member,
  organizationId,
  onClose,
  onSuccess,
}: RemoveMemberProps) {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const { removeMemberFromOrg } = useTeamApi(organizationId);
  const {
    submit,
    stepUpModalProps,
    isChallengeVisible,
    resetStepUpRecovery,
    handleStepUpProofTokenError,
  } = useStepUpSubmission({ isGatedRoleMutation: hasGatedRole(member.roles) });

  useEffect(() => {
    if (!visible) {
      return;
    }

    markStep('loaded_workspace_remove_member_modal');
  }, [visible]);

  const submitRemoval = useCallback(
    async (actionProofToken?: string) => {
      try {
        markStep('submit_workspace_member_removal');
        await removeMemberFromOrg({ memberUuid: member.userUuid, actionProofToken });
        logEvent('member_removed_success', {
          componentType: ComponentType.api,
          action: ActionType.api,
        });
        rumAddAction('member_removed_success');
        markStep('workspace_member_removal_success');
        resetStepUpRecovery();
        toast.show(formatMessage(messages.removeMemberModalSuccess));
        onClose();
        onSuccess?.();
      } catch (error) {
        logEvent('member_remove_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
        });
        rumAddAction('member_remove_failed');

        if (handleStepUpProofTokenError(error)) {
          return;
        }

        toast.show(formatMessage(messages.removeMemberModalError));
      }
    },
    [
      formatMessage,
      handleStepUpProofTokenError,
      member.userUuid,
      onClose,
      onSuccess,
      removeMemberFromOrg,
      resetStepUpRecovery,
      toast,
    ],
  );

  const handleRemoveMember = useCallback(() => {
    logEvent('member_removed_confirmed', {
      componentType: ComponentType.button,
      action: ActionType.click,
    });
    submit(submitRemoval);
  }, [submit, submitRemoval]);

  return (
    <>
      <RemoveMemberAlert
        visible={visible && !isChallengeVisible}
        onClose={onClose}
        memberName={getDisplayName(member)}
        onConfirm={handleRemoveMember}
      />
      <StepUpTwoFactorModal {...stepUpModalProps} />
    </>
  );
}
